import React, { useEffect, useState } from "react";
import { InputHelperText, useDataProvider, useInput, useTranslate } from "react-admin";

import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { SelectChangeEvent } from "@mui/material/Select";

import LinearProgress from "./LinerProgress";

import { PublicationPeriod } from "../pages/offers";
import { FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS } from "../utils/parameters";

const commonStyles = {
  outer: {
    marginBottom: "0px",
  },
  form: {
    width: 256,
  },
  view: {
    margin: "8px 0 4px",
    padding: "0 4px",
    width: "100%",
  },
  label: {
    position: "relative",
  },
  value: {
    padding: "8px 0 4px",
  },
};

const SelectItems = (props: {
  error: { message: string };
  input: {
    onChange: (v: unknown) => void;
    onBlur: () => void;
    value: string;
  };
  isRequired: boolean;
  items: PublicationPeriod[];
  label: string;
}) => {
  const { error, input, isRequired, items, label } = props;

  const requiredLabel = isRequired ? " *" : "";
  const handleChange = (ev: SelectChangeEvent) => {
    input.onChange(ev.target.value);
  };
  return (
    <FormControl variant="filled" sx={commonStyles.form} margin="dense" error={!!error}>
      <InputLabel id="label">{`${label}${requiredLabel}`}</InputLabel>
      <Select labelId="label" id="select" value={input.value} onChange={handleChange} onBlur={input.onBlur}>
        {items.map((item) => {
          return (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          );
        })}
      </Select>
      <FormHelperText variant="filled" error={!!error}>
        <InputHelperText touched={error} error={error} helperText="ui.offers.offer_publication_period_id_helper_text" />
      </FormHelperText>
    </FormControl>
  );
};

const RelationalValue = (props: { label: string; value: string }) => {
  const { label, value } = props;

  return (
    <div>
      <FormControl sx={commonStyles.view} margin="dense">
        <InputLabel shrink={true} sx={commonStyles.label}>
          {label}
        </InputLabel>
        <Box sx={commonStyles.value}>
          <Typography variant="body2">{value}</Typography>
        </Box>
      </FormControl>
    </div>
  );
};

export const SpotContractOfferPublicationPeriodInput = (props: any) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { field, isRequired, fieldState } = useInput(props);
  const { error } = fieldState;
  const [items, setItems] = useState<PublicationPeriod[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isCleanedUp = false;

    (async () => {
      if (!isCleanedUp) {
        setIsLoading(true);
      }

      const params = {
        ...FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS,
        sort: {
          ...FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS.sort,
          field: "publish_start_date",
          order: "ASC",
        },
        filter: {
          ...FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS.filter,
          is_selectable: true,
          is_deleted: false,
        },
      };
      const results = await dataProvider.getList("spot_contract_offer_publication_periods", params);

      if (!isCleanedUp) {
        // fetchに失敗すると結果がundefeindになるため
        if (results) {
          setItems(results.data);
        }

        setIsLoading(false);
      }
    })();

    return () => {
      isCleanedUp = true;
    };
  }, [dataProvider]);

  if (isLoading) {
    return (
      <div>
        <InputLabel shrink={true}>
          {translate(`resources.spot_contract_offers.fields.offer_publication_period_id`)}
        </InputLabel>
        <LinearProgress />
      </div>
    );
  }

  const selectedItem = items.find((item) => item.id === field.value);
  let startDate = null;
  let endDate = null;
  if (selectedItem) {
    startDate = selectedItem.publish_start_date.replace(/-/g, "/");
    endDate = selectedItem.publish_end_date.replace(/-/g, "/");
  }

  // @ts-ignore
  return (
    <Box sx={commonStyles.outer}>
      <SelectItems
        input={field}
        isRequired={isRequired}
        items={items}
        label={translate(`resources.spot_contract_offers.fields.offer_publication_period_id`)}
        error={!!error && error.message}
      />
      {startDate && endDate && (
        <>
          <RelationalValue
            label={translate(`resources.spot_contract_offers.fields.publish_start_date`)}
            value={startDate}
          />
          <RelationalValue
            label={translate(`resources.spot_contract_offers.fields.publish_end_date`)}
            value={endDate}
          />
        </>
      )}
    </Box>
  );
};
